/**
 * GoogleAuthCallback.js — one-shot exchange of the Emergent
 * `#session_id=<uuid>` URL fragment for our own JWT + user record.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 *
 * Rendered from App.js when the URL fragment contains a session_id.
 * Uses a `useRef` guard because React 18 StrictMode fires effects
 * twice — without it we'd POST the session_id twice and the second
 * call would 401 (session_ids are single-use).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleAuthCallback({ onLogin, onError }) {
  const processed = useRef(false);
  const [message, setMessage] = useState("Finishing your Google sign-in…");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const session_id = m ? decodeURIComponent(m[1]) : null;

    // Immediately strip the session_id from the URL so a hard reload
    // never replays it against the backend.
    try {
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + window.location.search
      );
    } catch {
      /* non-fatal */
    }

    if (!session_id) {
      onError && onError("Missing session_id from Google redirect.");
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/auth/google/session`,
          { session_id },
          { withCredentials: true, timeout: 20000 }
        );
        onLogin(data.user, data.access_token);
      } catch (e) {
        setMessage("Sign-in failed. Redirecting back to login…");
        onError &&
          onError(
            e?.response?.data?.detail ||
              e?.friendlyMessage ||
              e?.message ||
              "Google sign-in failed"
          );
      }
    })();
  }, [onLogin, onError]);

  return (
    <div
      data-testid="google-auth-callback"
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        color: "#FFD700",
        fontFamily: "Georgia, serif",
      }}
    >
      <div className="spinner" style={{ marginBottom: 18 }}></div>
      <div style={{ fontSize: "1.05rem" }}>{message}</div>
    </div>
  );
}
